import { Button, Modal, Popconfirm, Select, Spin, Tag } from 'antd';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Assets } from '../assets';
import { Endpoints, getResourceUrl } from '../constants/endpoint.constant';
import { ClientStatus, isRegionalManagerRole, TRIP_STATUSES, TripStatus } from '../constants/general.constant';
import { DRIVER_DOCUMENTS } from '../constants/driver_documents.constant';
import { useGet, usePost } from '../hooks/useRest.hook';
import { DriverInfringementsModal } from './driver_infringements_modal.component';
import { UserContext } from '../App';
import { formatDateZA, formatTimeZA } from '../utils/date.util';

const STATUS = 'Status';
const SUPPORTED_FILE_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
const IMAGE_FILE_PATTERN = /\.(png|jpe?g)(?:$|[?#])/i;
const PDF_FILE_PATTERN = /\.pdf(?:$|[?#])/i;

function buildPdfViewerUrl(url) {
    if (!url) return '';
    return `${url}#toolbar=0&navpanes=0&scrollbar=0&page=1&view=FitH`;
}

function getDocumentKind(source = '') {
    const value = (source || '').toString().toLowerCase();
    if (value.includes('application/pdf') || PDF_FILE_PATTERN.test(value)) return 'pdf';
    if (value.includes('image/png') || value.includes('image/jpeg') || IMAGE_FILE_PATTERN.test(value)) return 'image';
    return 'unknown';
}

function resolveDocumentUrl(driver, document) {
    const primary = driver?.[document.key];
    if (primary) return getResourceUrl(primary);

    for (const legacyKey of document.fallbackKeys || []) {
        if (driver?.[legacyKey]) {
            return getResourceUrl(driver[legacyKey]);
        }
    }

    return '';
}

function getDriverDocumentCompletion(driver) {
    const uploadedCount = DRIVER_DOCUMENTS.filter((document) => !!resolveDocumentUrl(driver, document)).length;
    return {
        uploadedCount,
        totalCount: DRIVER_DOCUMENTS.length,
        isComplete: uploadedCount === DRIVER_DOCUMENTS.length,
    };
}

function getDriverRegionName(driver, origins = []) {
    const directName = driver?.user?.origin?.name || driver?.origin?.name;
    if (directName) return directName;

    const originId = (driver?.user?.originId || driver?.originId || '').toString();
    if (!originId) return '';

    return origins.find(origin => (origin?.id || '').toString() === originId)?.name || '';
}

function getDriverLicenceExpiryMs(driver = {}) {
    const direct = Number(driver?.driverLicenceExpiryDateMs ?? driver?.user?.driverLicenceExpiryDateMs);
    if (Number.isFinite(direct) && direct > 0) return direct;
    const value = driver?.driverLicenceExpiryDate || driver?.user?.driverLicenceExpiryDate;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function getDriverLicenceStatus(driver = {}) {
    const expiryMs = getDriverLicenceExpiryMs(driver);
    if (!expiryMs) {
        return {
            label: 'Licence expiry missing',
            dateLabel: 'Not set',
            className: 'bg-[#eef1f4] text-[#67727e]',
            dotClassName: 'bg-[#9aa3ad]',
        };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysUntilExpiry = Math.ceil((expiryMs - today.getTime()) / (24 * 60 * 60 * 1000));
    const dateLabel = formatDateZA(expiryMs);

    if (daysUntilExpiry < 0) {
        return {
            label: 'Licence expired',
            dateLabel,
            className: 'bg-red-100 text-red-700',
            dotClassName: 'bg-red-500',
        };
    }
    if (daysUntilExpiry <= 30) {
        return {
            label: `Expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}`,
            dateLabel,
            className: 'bg-orange-100 text-orange-700',
            dotClassName: 'bg-orange-500',
        };
    }
    if (daysUntilExpiry <= 90) {
        return {
            label: `Expires in ${daysUntilExpiry} days`,
            dateLabel,
            className: 'bg-yellow-100 text-yellow-700',
            dotClassName: 'bg-yellow-500',
        };
    }
    return {
        label: 'Licence valid',
        dateLabel,
        className: 'bg-green-100 text-green-700',
        dotClassName: 'bg-green-500',
    };
}

function buildDocumentFileName(driverName, label) {
    const normalizedDriver = (driverName || 'driver')
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'driver';
    const normalizedLabel = label
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'document';

    return `${normalizedDriver}-${normalizedLabel}`;
}

function extractFileExtension(documentUrl) {
    const decoded = decodeURIComponent((documentUrl || '').split('?')[0] || '');
    const match = decoded.match(/\.([a-z0-9]+)$/i);
    return match?.[1]?.toLowerCase() || null;
}

function buildDownloadFileName(driverName, label, documentUrl) {
    const base = buildDocumentFileName(driverName, label);
    const extension = extractFileExtension(documentUrl) || (getDocumentKind(documentUrl) === 'image' ? 'jpg' : 'pdf');
    return `${base}.${extension}`;
}

function triggerBrowserDownload(url, fileName) {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noreferrer';
    if (fileName) {
        link.download = fileName;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

const DocumentActionButton = ({ src, label, onClick, disabled = false, loading = false, tone = 'default' }) => (
    <button
        type="button"
        onClick={disabled ? undefined : onClick}
        disabled={disabled || loading}
        title={label}
        className={`flex h-11 w-11 items-center justify-center rounded-2xl border transition ${tone === 'accent' ? 'border-[#d7643b] bg-[#fff1ea]' : 'border-[#d9d9d9] bg-white'} ${disabled || loading ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:-translate-y-0.5 hover:border-[#d7643b] hover:shadow-[0_12px_28px_rgba(215,100,59,0.18)]'}`}
    >
        {loading ? (
            <Spin size="small" />
        ) : (
            <img alt={label} src={src} className="h-7 w-7 object-contain" />
        )}
    </button>
);

const DriverDocumentCard = ({
    document,
    documentUrl,
    uploadBusy,
    replaceBusy,
    downloadBusy,
    onUpload,
    onReplace,
    onView,
    onDownload,
    canManage = true,
}) => {
    const uploaded = !!documentUrl;
    const documentKind = getDocumentKind(documentUrl);

    return (
        <div className={`group relative flex min-h-[320px] flex-col gap-4 overflow-hidden rounded-[28px] border p-5 transition ${uploaded ? 'border-[#f0d2c6] bg-gradient-to-br from-white via-[#fffaf7] to-[#fff2eb] shadow-[0_18px_48px_rgba(216,104,63,0.12)]' : 'border-[#ececec] bg-white shadow-[0_12px_36px_rgba(15,23,42,0.06)]'} hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(216,104,63,0.16)]`}>
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#ffb48f] via-[#d7643b] to-[#ffb48f] opacity-80" />
            <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-2">
                    <span className="text-lg font-bold leading-tight text-[#1f1f1f]">{document.label}</span>
                    <div className="flex items-center gap-2 text-xs">
                        <Tag className={`m-0 rounded-full border-0 px-3 py-1 font-medium ${uploaded ? 'bg-[#1d9f61] text-white' : 'bg-[#eef1f4] text-[#67727e]'}`}>
                            {uploaded ? 'Uploaded' : 'Waiting'}
                        </Tag>
                        {uploaded && (
                            <Tag className="m-0 rounded-full border border-[#f1cfbf] bg-white px-3 py-1 font-medium text-[#c55d36]">
                                {documentKind === 'image' ? 'Image' : documentKind === 'pdf' ? 'PDF' : 'File'}
                            </Tag>
                        )}
                    </div>
                </div>
                <span
                    title={uploaded ? 'Document uploaded' : 'No document uploaded'}
                    className={`mt-1 inline-block h-4 w-4 rounded-full border-[3px] border-white shadow ${uploaded ? 'bg-lime-400' : 'bg-[#d9d9d9]'}`}
                />
            </div>
            <div className={`flex h-40 items-center justify-center overflow-hidden rounded-[22px] border ${uploaded ? 'border-[#edd0c2] bg-white' : 'border-[#ececec] bg-[#fafafa]'}`}>
                {uploaded ? (
                    documentKind === 'image' ? (
                        <img
                            alt={`${document.label} preview`}
                            src={documentUrl}
                            className="h-full w-full object-cover"
                        />
                    ) : (
                        <iframe
                            title={`${document.label} preview`}
                            src={buildPdfViewerUrl(documentUrl)}
                            className="h-full w-full bg-white"
                        />
                    )
                ) : (
                    <div className="flex flex-col items-center gap-2 px-4 text-center text-xs text-[#8c8c8c]">
                        <img alt="" src={Assets.Pdf} className="h-10 w-10 object-contain opacity-60" />
                        <span>No document uploaded</span>
                        <span className="text-[11px] uppercase tracking-[0.16em] text-[#b7b7b7]">PDF, JPG, PNG</span>
                    </div>
                )}
            </div>
            <div className="mt-auto flex items-center justify-between gap-3 pt-1">
                <div className="text-xs leading-relaxed text-[#7d7d7d]">
                    {uploaded
                        ? canManage ? 'Preview, replace or download the current file.' : 'Preview or download the current file.'
                        : canManage ? 'Upload the driver document to store it in Firebase.' : 'No document uploaded.'}
                </div>
                <div className="flex flex-wrap gap-3">
                {canManage && <>
                <DocumentActionButton
                    src={Assets.Upload}
                    label={`Upload ${document.label}`}
                    onClick={onUpload}
                    disabled={uploaded}
                    loading={uploadBusy}
                    tone="accent"
                />
                <DocumentActionButton
                    src={Assets.EditSquare}
                    label={`Replace ${document.label}`}
                    onClick={onReplace}
                    disabled={!uploaded}
                    loading={replaceBusy}
                />
                </>}
                <DocumentActionButton
                    src={Assets.EyeSquare}
                    label={`View ${document.label}`}
                    onClick={onView}
                    disabled={!uploaded}
                />
                <DocumentActionButton
                    src={Assets.DownloadSquare}
                    label={`Download ${document.label}`}
                    onClick={onDownload}
                    disabled={!uploaded}
                    loading={downloadBusy}
                />
                </div>
            </div>
        </div>
    );
};

export const DriverCard = ({ driver, refresh, totalTripCount, origins = [] }) => {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const selectedDocumentRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [infringementsModal, setInfringementsModal] = useState(false);
    const [viewerDocument, setViewerDocument] = useState(null);
    const [downloadingKey, setDownloadingKey] = useState(null);
    const [uploadingDocument, setUploadingDocument] = useState(null);
    const uploadRq = usePost(Endpoints.UPDATE_DRIVER(driver?.id));
    const deleteRq = usePost(Endpoints.DELETE_DRIVER(driver?.id));
    const tripRq = useGet(Endpoints.GET_DRIVER_TRIP(driver?.id), false);
    const tripLoadRef = useRef(tripRq?.load);
    const documentCompletion = getDriverDocumentCompletion(driver);
    const driverRegionName = getDriverRegionName(driver, origins);
    const licenceStatus = getDriverLicenceStatus(driver);
    const canManageDriver = !isRegionalManagerRole(user);

    tripLoadRef.current = tripRq?.load;

    useEffect(() => {
        if (expanded) {
            tripLoadRef.current?.();
        }
    }, [expanded]);

    const openDocumentPicker = (document, action) => {
        selectedDocumentRef.current = document;
        setUploadingDocument({ key: document.key, action });
        if (inputRef.current) {
            inputRef.current.value = '';
            inputRef.current.click();
        }
    };

    const updateDriverStatus = async (status) => {
        try {
            const formData = new FormData();
            formData.append(STATUS, status);
            await uploadRq.put(formData);
            refresh();
        } catch (error) {
            console.error('[DRIVER STATUS UPDATE] error', error);
        }
    };

    const deleteDriver = async () => {
        try {
            await deleteRq.post({});
            refresh();
        } catch (error) {
            console.error('[DELETE DRIVER] error', error);
        }
    };

    const uploadFile = async (event) => {
        const selectedDocument = selectedDocumentRef.current;
        const file = event?.target?.files?.[0];
        if (!selectedDocument || !file) return;

        const isSupported = SUPPORTED_FILE_TYPES.includes(file.type) || /\.(pdf|png|jpe?g)$/i.test(file.name || '');
        if (!isSupported) {
            toast.error('Only PDF, JPG and PNG documents are supported.');
            event.target.value = '';
            setUploadingDocument(null);
            return;
        }

        try {
            const formData = new FormData();
            formData.append(selectedDocument.uploadField, file);
            await uploadRq.put(formData);
            refresh();
        } catch (error) {
            console.error('[DRIVER DOCUMENT UPLOAD] error', error);
        } finally {
            setUploadingDocument(null);
            event.target.value = '';
        }
    };

    const downloadDocument = async (document, documentUrl) => {
        if (!documentUrl) return;

        const fileName = buildDownloadFileName(driver?.user?.name, document.label, documentUrl);

        try {
            setDownloadingKey(document.key);
            const response = await fetch(documentUrl);
            if (!response.ok) {
                throw new Error(`Download failed with status ${response.status}`);
            }

            const blob = await response.blob();
            saveAs(blob, fileName);
        } catch (error) {
            console.error('[DRIVER DOCUMENT DOWNLOAD] error', error);
            triggerBrowserDownload(documentUrl, fileName);
        } finally {
            setDownloadingKey(null);
        }
    };

    return <div className="flex flex-col border border-solid border-black px-16 py-5">
        <DriverInfringementsModal driver={driver} open={infringementsModal} onCancel={setInfringementsModal.bind(this, false)} />
        <Modal
            width={960}
            title={viewerDocument?.label || 'Document preview'}
            open={!!viewerDocument}
            footer={null}
            destroyOnHidden
            onCancel={() => setViewerDocument(null)}
        >
            {viewerDocument?.url ? (
                <div className="flex flex-col gap-4">
                    <div className="flex justify-end">
                        <Button
                            type="primary"
                            icon={<img alt="" src={Assets.DownloadSquare} className="h-4 w-4 object-contain" />}
                            onClick={() => downloadDocument(viewerDocument.document, viewerDocument.url)}
                            loading={downloadingKey === viewerDocument?.document?.key}
                        >
                            Download
                        </Button>
                    </div>
                    {getDocumentKind(viewerDocument.url) === 'image' ? (
                        <div className="flex max-h-[70vh] items-center justify-center overflow-hidden rounded-[24px] border border-[#e3d0c7] bg-[#fff8f4] p-4">
                            <img
                                alt={`${viewerDocument?.label} popup preview`}
                                src={viewerDocument.url}
                                className="max-h-[64vh] w-auto max-w-full rounded-[18px] object-contain shadow-[0_18px_44px_rgba(0,0,0,0.12)]"
                            />
                        </div>
                    ) : (
                        <iframe
                            title={`${viewerDocument?.label} popup preview`}
                            src={buildPdfViewerUrl(viewerDocument.url)}
                            className="h-[70vh] w-full rounded-[24px] border border-[#d9d9d9] bg-white"
                        />
                    )}
                </div>
            ) : null}
        </Modal>
        <div className={`flex flex-row items-center gap-5 border-b border-transparent ${expanded ? 'border-b-black border-solid' : ''}`}>
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border border-[#e4d4cb] bg-[#fff7f2] shadow-[0_10px_24px_rgba(215,100,59,0.12)]">
                <img alt="" className="h-full w-full object-cover" src={getResourceUrl(driver?.user?.picutre)} />
            </div>

            <div className="grid flex-1 grid-cols-6 gap-5 py-5">
                <span className="font-bold">Driver name</span>
                <span className="font-bold">Region</span>
                <span className="font-bold">Number</span>
                <span className="font-bold">Email</span>
                <span className="font-bold">Licence Expiry</span>
                <span className="font-bold">Actions</span>
                <span>{driver?.user?.name}</span>
                <span>{driverRegionName || 'Not set'}</span>
                <span>{driver?.user?.number}</span>
                <span>{driver?.user?.email}</span>
                <span className="flex flex-col gap-1">
                    <Tag className={`m-0 w-fit rounded-full border-0 px-3 py-1 font-semibold ${licenceStatus.className}`}>
                        <span className={`mr-2 inline-block h-2 w-2 rounded-full ${licenceStatus.dotClassName}`} />
                        {licenceStatus.label}
                    </Tag>
                    <span className="text-xs text-[#6e6e6e]">{licenceStatus.dateLabel}</span>
                </span>
                <div className="flex flex-row items-center gap-3">
                    <span
                        title={documentCompletion.isComplete
                            ? `All ${documentCompletion.totalCount} driver documents uploaded`
                            : `${documentCompletion.uploadedCount} of ${documentCompletion.totalCount} driver documents uploaded`}
                        className={`inline-block h-4 w-4 rounded-full border-2 border-white shadow ${documentCompletion.isComplete ? 'bg-lime-400' : 'bg-[#cfd4da]'}`}
                    />
                    {canManageDriver && <img alt="" src={Assets.EditSquare} onClick={navigate?.bind(this, `/create-driver/${driver?.userId}`)} className="h-6 w-6 cursor-pointer" />}
                </div>
                <div />
                <div />
                <div />
                <div />
                <div />
                <Button loading={deleteRq?.loading} onClick={setExpanded.bind(this, !expanded)} type="primary">View {expanded ? 'Less' : 'More'}</Button>
            </div>
        </div>
        {expanded && <>
            <div className="flex flex-col gap-6 border-b border-transparent border-b-black border-solid py-6">
                <input type="file" accept="application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg" hidden ref={inputRef} onChange={uploadFile} />
                <div className="flex flex-row items-center justify-between">
                    <div className="flex flex-col gap-2">
                        <span className="self-start text-xl font-bold text-[#1c1c1c]">Driver Personal Information</span>
                        <span className="text-sm text-[#6e6e6e]">All files are linked to the driver record and stored in Firebase. Supported formats: PDF, JPG and PNG.</span>
                    </div>
                    <Button type="primary" onClick={setInfringementsModal.bind(this, true)}>Infringements</Button>
                </div>

                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                    <div className="rounded-[28px] border border-[#f0d2c6] bg-gradient-to-br from-[#fff7f2] to-white p-5 shadow-[0_18px_48px_rgba(216,104,63,0.12)]">
                        <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#b85d38]">Driver activity</span>
                        <span className="mt-4 block text-sm font-medium text-[#666]">Number of Completed Trips</span>
                        <span className="mt-2 block text-4xl font-bold text-[#1a1a1a]">{totalTripCount}</span>
                    </div>
                    <div className="rounded-[28px] border border-[#dfe7ef] bg-gradient-to-br from-white to-[#f8fbff] p-5 shadow-[0_18px_48px_rgba(26,63,99,0.08)]">
                        <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#4a6b8a]">Profile state</span>
                        <span className="mb-3 mt-4 block text-sm font-bold text-[#666]">Registration Status</span>
                        {canManageDriver ? <Select
                            loading={uploadRq?.loading}
                            value={driver?.status}
                            options={ClientStatus.map(M => ({ label: M.label, value: M.id }))}
                            placeholder="Driver Status"
                            onChange={updateDriverStatus}
                        /> : <span className="text-base font-semibold text-[#1a1a1a]">
                            {ClientStatus.find(status => status.id === driver?.status)?.label || driver?.status || 'Not set'}
                        </span>}
                    </div>
                    <div className="rounded-[28px] border border-[#ead8c7] bg-gradient-to-br from-white to-[#fffaf5] p-5 shadow-[0_18px_48px_rgba(168,105,52,0.08)] lg:col-span-2">
                        <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#9b6333]">Driver licence</span>
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                            <Tag className={`m-0 rounded-full border-0 px-4 py-2 text-sm font-semibold ${licenceStatus.className}`}>
                                <span className={`mr-2 inline-block h-2.5 w-2.5 rounded-full ${licenceStatus.dotClassName}`} />
                                {licenceStatus.label}
                            </Tag>
                            <span className="text-sm font-semibold text-[#1a1a1a]">Expiry: {licenceStatus.dateLabel}</span>
                        </div>
                        {canManageDriver ? <Button className="mt-4" onClick={navigate?.bind(this, `/create-driver/${driver?.userId}`)}>
                            Update licence expiry
                        </Button> : null}
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                    {DRIVER_DOCUMENTS.map((document) => {
                        const documentUrl = resolveDocumentUrl(driver, document);
                        return (
                            <DriverDocumentCard
                                key={document.key}
                                document={document}
                                documentUrl={documentUrl}
                                uploadBusy={uploadRq?.loading && uploadingDocument?.key === document.key && uploadingDocument?.action === 'upload'}
                                replaceBusy={uploadRq?.loading && uploadingDocument?.key === document.key && uploadingDocument?.action === 'replace'}
                                downloadBusy={downloadingKey === document.key}
                                onUpload={() => openDocumentPicker(document, 'upload')}
                                onReplace={() => openDocumentPicker(document, 'replace')}
                                onView={() => setViewerDocument({ document, label: document.label, url: documentUrl })}
                                onDownload={() => downloadDocument(document, documentUrl)}
                                canManage={canManageDriver}
                            />
                        );
                    })}
                </div>
                {canManageDriver && <div className="flex justify-end pt-2">
                    <Popconfirm
                        title="Delete driver"
                        description="This will permanently remove the driver profile and uploaded documents."
                        okText="Delete"
                        okButtonProps={{ danger: true, loading: deleteRq?.loading }}
                        cancelText="Cancel"
                        onConfirm={deleteDriver}
                    >
                        <Button danger type="default">
                            Delete Driver
                        </Button>
                    </Popconfirm>
                </div>}
            </div>
            {tripRq?.data?.id &&
                <div className="flex flex-col gap-5 py-5">
                    <span className="self-start flex-bold">Active Trip</span>
                    <div className="flex flex-col gap-5 border border-solid p-7">
                        <div className="grid grid-cols-7 gap-5">
                            <span className="font-bold">Vehicle Reg No</span>
                            <span className="font-bold">Pickup Location</span>
                            <span className="font-bold">Dropoff Location</span>
                            <span className="font-bold">Start Date</span>
                            <span className="font-bold">Start Time</span>
                            <span className="font-bold">Total KM</span>
                            <span className="font-bold">MYC Ref No</span>
                            <span>{tripRq?.data?.vehicleRegNumber}</span>
                            <span>{tripRq?.data?.pickUp}</span>
                            <span>{tripRq?.data?.dropOff}</span>
                            <span>{formatDateZA(tripRq?.data?.startDateTime)}</span>
                            <span>{formatTimeZA(tripRq?.data?.startDateTime)}</span>
                            <span>{tripRq?.data?.totalKM?.toFixed?.(0)}</span>
                            <span>MYC#{tripRq?.data?.id}</span>
                        </div>
                        <div className="flex flex-col gap-5 border-b border-transparent border-b-black border-solid py-5">
                            <span className="self-start flex-bold">Trip Order Live Status</span>

                            <span className="font-bold">Trip Status <span className="text-red-500">{TRIP_STATUSES[tripRq?.data?.status]}
                            </span></span>
                            <div className="mt-4 flex flex-row justify-between px-5">
                                <span className={tripRq?.data?.status === TripStatus.DriverAccepted ? 'text-red-500' : ''}>1 - Order Accepted</span>
                                <span className={tripRq?.data?.status === TripStatus.PreInspection ? 'text-red-500' : ''}>2 - Pre inspection</span>
                                <span className={tripRq?.data?.status === TripStatus.TripStarted ? 'text-red-500' : ''}>3 - On Route</span>
                                <span className={tripRq?.data?.status === TripStatus.PostInspection ? 'text-red-500' : ''}>4 - Post inspection</span>
                                <span className={tripRq?.data?.status === TripStatus.Completed ? 'text-red-500' : ''}>5 - Order Completed</span>
                            </div>
                        </div>
                    </div>
                </div>}
        </>}
    </div>;
};
